import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import { SITE_URL } from '../../lib/seo';
import { tArray } from '../../lib/i18nSafe';

export default function Models() {
  const { t } = useTranslation();
  const models = tArray(t, 'models.catalog');

  return (
    <Box>
      <Helmet>
        <title>Models | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/models`} />
      </Helmet>
      <PageHeader
        eyebrow={t('models.eyebrow')}
        title={t('models.title')}
        body={t('models.body')}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        <Paper
          variant="outlined"
          role="note"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 2,
            borderStyle: 'dashed',
            bgcolor: 'background.default'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'text.secondary',
              mb: 0.5
            }}
          >
            {t('models.eyebrow')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem' }}>
            {t('models.disclaimer')}
          </Typography>
        </Paper>

        <Stack spacing={3}>
          {models.map((m) => (
            <Paper
              key={m.id}
              variant="outlined"
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                bgcolor: 'background.paper'
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip label={m.id} size="small" sx={{ fontFamily: 'var(--font-mono)' }} />
                    <Chip label={m.family} size="small" variant="outlined" />
                  </Stack>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '1.9rem',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                      mb: 0.5
                    }}
                  >
                    {m.name}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>{m.tagline}</Typography>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: 'text.secondary',
                          mb: 0.5
                        }}
                      >
                        {t('models.headers.context')}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-mono)' }}>{m.context}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: 'text.secondary',
                          mb: 0.5
                        }}
                      >
                        {t('models.headers.latency')}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-mono)' }}>{m.latency}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: 'text.secondary',
                          mb: 0.5
                        }}
                      >
                        {t('models.headers.inputPrice')}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-mono)' }}>{m.inputPrice}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: 'text.secondary',
                          mb: 0.5
                        }}
                      >
                        {t('models.headers.outputPrice')}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-mono)' }}>{m.outputPrice}</Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {(m.tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, color: 'text.secondary' }}>
                    <Typography
                      sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'text.secondary' }}
                    >
                      {t('models.batchNote')}
                    </Typography>
                    <ArrowForwardRoundedIcon fontSize="small" />
                  </Stack>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {m.body}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
